import { Lock } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { AccessScopeBadge } from "@/modules/dashboard/settings/components/access/AccessScopeBadge";
import { roleColor } from "@/modules/dashboard/settings/utils/roleColor";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { RoleRow } from "../hooks/useRolesCatalog";
import { useRoleReach } from "../utils/roleReach";
import { RolePoliciesCell } from "./RolePoliciesCell";

export interface RolesTableProps {
  rows: readonly RoleRow[];
  loading: boolean;
  empty: ReactNode;
  footer?: ReactNode;
  /** Staff and org admins get different — and equally correct — member tallies; see the members column. */
  isInternalUser: boolean;
  onOpen: (row: RoleRow) => void;
}

/**
 * The catalog at `lg` and up; below that the same rows render as cards.
 *
 * No sorting: `GET /internal/roles` fixes the order at `created_at DESC, id DESC` and accepts no `sort`, so
 * no header is clickable. A sort control that does not sort is worse than none.
 *
 * The whole row opens the editor — the same drawer Settings opens, in the same read-only state when the role
 * is protected — so there is no actions column to hold a single button.
 */
export function RolesTable({ rows, loading, empty, footer, isInternalUser, onOpen }: RolesTableProps) {
  const { t } = useTranslations("settings");
  const reachOf = useRoleReach();

  const columns = useMemo<ColocaDataTableColumn<RoleRow>[]>(
    () => [
      {
        id: "role",
        header: t("access_roles_col_role"),
        headerClassName: "min-w-[260px]",
        cell: ({ role }) => (
          <div className="flex items-start gap-2">
            <span
              className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
              style={{ background: roleColor(role.slug) }}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-[13px] font-semibold text-foreground">{role.name}</span>
                {role.is_protected && (
                  <Lock
                    size={11}
                    className="shrink-0 text-muted-foreground/70"
                    aria-label={t("access_roles_protected_hint")}
                  />
                )}
                {role.is_default && (
                  <ColocaBadge variant="secondary">{t("access_role_default_badge")}</ColocaBadge>
                )}
              </div>
              <div className="truncate font-mono text-[10.5px] text-muted-foreground">{role.slug}</div>
            </div>
          </div>
        ),
      },
      {
        id: "scope",
        header: t("access_page_col_scope"),
        headerClassName: "w-[110px]",
        cell: ({ role }) => <AccessScopeBadge organizationId={role.organization_id} />,
      },
      {
        id: "policies",
        header: t("access_roles_col_policies"),
        headerClassName: "min-w-[220px]",
        cell: ({ role }) => <RolePoliciesCell policies={role.policies} />,
      },
      {
        id: "reach",
        header: t("access_roles_col_reach"),
        headerClassName: "w-[190px]",
        cell: (row) => {
          const reach = reachOf(row.role);
          return (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <reach.icon size={12} className="shrink-0" />
              <span className="truncate">{reach.label}</span>
            </span>
          );
        },
      },
      {
        id: "members",
        header: t("access_roles_col_members"),
        headerClassName: "w-[150px]",
        // Tenancy-aware on purpose: the backend scopes `member_count` to the caller's organization, so a
        // global role reports a different (and equally correct) number to staff and to an org admin. An
        // unqualified "N asignaciones" would be false for one of the two readers.
        cell: ({ role }) => (
          <span className="font-mono text-[11px] text-muted-foreground">
            {isInternalUser
              ? role.member_count === 1
                ? t("access_role_member_count_platform_one")
                : t("access_role_member_count_platform", { count: role.member_count })
              : role.member_count === 1
                ? t("access_role_member_count_org_one")
                : t("access_role_member_count_org", { count: role.member_count })}
          </span>
        ),
      },
    ],
    [t, reachOf, isInternalUser],
  );

  return (
    <ColocaDataTable<RoleRow>
      columns={columns}
      rows={rows as RoleRow[]}
      rowKey={(row) => row.role.id}
      onRowClick={onOpen}
      loading={loading}
      skeletonRows={8}
      empty={empty}
      footer={footer}
      // The page already wraps this in a `ColocaSurface`; a second frame would double the hairline and notch
      // every corner. Sum of the declared column widths — below it the table scrolls.
      frame="none"
      minWidth={930}
      // Dim, never tint: a read-only row is still a row, and colouring by state turns the table into a
      // colour chart the eye stops reading.
      rowClassName={(row) => (row.editable ? undefined : "opacity-70")}
    />
  );
}
